import { createApi } from "@reduxjs/toolkit/dist/query/react";
import { baseQuery } from "@/redux/baseQuery";
import {
  CardLabel,
  CardToggles,
  ClientPaymentMethod,
  ClientPaymentMethodArgs,
  GetClientPaymentMethodsRawResponse,
  GetClientPaymentProviderKeysArgs,
  GetClientPaymentProviderKeysRawResponse,
  UpdateCardNicknameArgs,
} from "./types";

/**
 * Client portal payment methods.
 * Same surface the mobile app uses: list/get/delete are v3 account-management
 * endpoints; set-default is still the legacy v1 endpoint (GET by design of the
 * legacy API). Card creation goes through Stripe SetupIntent confirmation —
 * the backend persists the card via webhook, confirmed by getClientPaymentMethod
 * with the SetupIntent id (see PaymentMethodSetupPanel for the same flow).
 */
export const clientPaymentApi = createApi({
  reducerPath: "clientPaymentApi",
  baseQuery,
  tagTypes: ["ClientPaymentMethods"],
  endpoints: (builder) => ({
    getClientPaymentMethods: builder.query<ClientPaymentMethod[], string>({
      query: (accountId) => `/v3/account-management/${accountId}/payment-method`,
      transformResponse: (response: GetClientPaymentMethodsRawResponse) =>
        response.total_items > 0 ? response._embedded.payment_method : [],
      providesTags: ["ClientPaymentMethods"],
    }),
    getClientPaymentMethod: builder.query<ClientPaymentMethod, ClientPaymentMethodArgs>({
      // paymentMethodId also accepts a Stripe SetupIntent id (seti_*) — used to
      // confirm the backend has persisted a freshly added card
      query: ({ accountId, paymentMethodId }) => `/v3/account-management/${accountId}/payment-method/${paymentMethodId}`,
    }),
    /** @deprecated DEV-1136 — read `toggles` from the v3 client profile (useGetClientProfileQuery) instead */
    getCardToggles: builder.query<CardToggles, void>({
      // same source the mobile app reads card type availability from — e.g. personal cards are
      // disabled for company passengers with invoicing when the provider forbids mixing
      query: () => "/v2/profile/me",
      transformResponse: (response: { toggles?: CardToggles }) => ({
        card_personal_enabled: response.toggles?.card_personal_enabled ?? true,
        card_corporate_enabled: response.toggles?.card_corporate_enabled ?? true,
      }),
    }),
    getClientPaymentProviderKeys: builder.query<GetClientPaymentProviderKeysRawResponse, GetClientPaymentProviderKeysArgs>({
      // allow_multiple opts out of the backend's legacy replace-on-add behaviour (DEV-1136),
      // letting an account hold several cards of the same label (max 5 enforced server-side).
      // DEV-1142: label is optional — backend auto-assigns type when omitted.
      query: ({ accountId, label }) =>
        `/v3/account-management/${accountId}/payment-method/keys/stripe?allow_multiple=1${label ? `&label=${label}` : ""}`,
    }),
    deleteClientPaymentMethod: builder.mutation<null, ClientPaymentMethodArgs>({
      query: ({ accountId, paymentMethodId, replacementId }) => ({
        url: `/v3/account-management/${accountId}/payment-method/${paymentMethodId}${
          replacementId ? `?replacement_payment_method_uuid=${replacementId}` : ""
        }`,
        method: "DELETE",
      }),
      invalidatesTags: ["ClientPaymentMethods"],
    }),
    setDefaultPaymentMethod: builder.mutation<ClientPaymentMethod, ClientPaymentMethodArgs>({
      query: ({ accountId, paymentMethodId }) => ({
        url: `/v3/account-management/${accountId}/payment-method/${paymentMethodId}`,
        method: "PATCH",
        body: { default: true },
      }),
      // Flip the toggle optimistically; the invalidated refetch re-syncs with server truth,
      // and the patch is rolled back if the request fails
      async onQueryStarted({ accountId, paymentMethodId }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          clientPaymentApi.util.updateQueryData("getClientPaymentMethods", accountId, (draft) => {
            draft.forEach((method) => {
              method.default = method.uuid === paymentMethodId;
            });
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patch.undo();
        }
      },
      invalidatesTags: ["ClientPaymentMethods"],
    }),
    updateCardNickname: builder.mutation<ClientPaymentMethod, UpdateCardNicknameArgs>({
      query: ({ accountId, paymentMethodId, nickname }) => ({
        url: `/v3/account-management/${accountId}/payment-method/${paymentMethodId}`,
        method: "PATCH",
        body: { nickname },
      }),
      invalidatesTags: ["ClientPaymentMethods"],
    }),
    /** @deprecated superseded by setDefaultPaymentMethod (v3 PATCH by uuid, DEV-1136) — the v1 endpoint
     * addresses cards by type and picks an arbitrary card when several share a label */
    setDefaultCardType: builder.mutation<void, CardLabel>({
      // Legacy endpoint — GET is the verb the v1 API exposes for this action
      query: (label) => ({ url: `/v1/card/default/${label}`, method: "GET" }),
      invalidatesTags: ["ClientPaymentMethods"],
    }),
  }),
});

export const {
  useGetCardTogglesQuery,
  useGetClientPaymentMethodsQuery,
  useLazyGetClientPaymentMethodQuery,
  useLazyGetClientPaymentProviderKeysQuery,
  useDeleteClientPaymentMethodMutation,
  useSetDefaultPaymentMethodMutation,
  useUpdateCardNicknameMutation,
} = clientPaymentApi;
